import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { backendConnector } from "../backendIntegration/lambdas";
import logger from "../utils/logger";
import { withoutTrailingSlash } from "../utils/nextStageMessages";
import {
  getAuthHeaders,
  FORM_URLENCODED_CONTENT_TYPE,
} from "../backendIntegration/authHeaders";
import { isJobsUnavailable } from "../backendIntegration/apiErrors";

let currentJobDetailsAbortController = null;

// --- 1) Thunk to fetch initially applied jobs ---
export const fetchAppliedJobData = createAsyncThunk(
  "jobs/fetchAppliedJobData",
  async (_, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders({
        contentType: FORM_URLENCODED_CONTENT_TYPE,
        includeIdentityId: false,
      });
      const response = await axios.get(
        `${process.env.REACT_APP_API_DOMAIN}/getAppliedJobs`,
        {
          headers: headers,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- 1a) Silent refresh for applied jobs (no UI loading toggles) ---
export const refreshAppliedJobDataSilently = createAsyncThunk(
  "jobs/refreshAppliedJobDataSilently",
  async (_, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders({
        contentType: FORM_URLENCODED_CONTENT_TYPE,
        includeIdentityId: false,
      });
      const response = await axios.get(
        `${process.env.REACT_APP_API_DOMAIN}/getAppliedJobs`,
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- 2) Fetch job details (can show loading in the UI) ---
export const fetchJobDetails = createAsyncThunk(
  "jobs/fetchJobDetails",
  async ({ jobUrl, companyId }, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders({
        contentType: FORM_URLENCODED_CONTENT_TYPE,
        includeIdentityId: false,
      });
      const companyIdString = companyId
        ? `&companyId=${encodeURIComponent(companyId)}`
        : "";
      const abortController = new AbortController();
      currentJobDetailsAbortController = abortController;
      const response = await axios.get(
        `${process.env.REACT_APP_API_DOMAIN}/getJobDetails?jobUrl=${encodeURIComponent(jobUrl)}${companyIdString}`,
        {
          headers: headers,
          signal: abortController.signal,
        }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue("Request canceled");
      }
      // Job DB outage: carry the server's message so the page says that, not "Something went wrong".
      if (error.response?.status === 503 && isJobsUnavailable(error.response.data)) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    } finally {
      currentJobDetailsAbortController = null;
    }
  }
);

// --- 3) Thunk to update your backend silently ---
export const updateAppliedJobs = createAsyncThunk(
  "jobs/updateAppliedJobs",
  async (jobToMove, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders({ includeIdentityId: false });

      if (jobToMove.newStatus === "applying") jobToMove.newStatus = "applyingJobs";
      if (jobToMove.newStatus === "applied") jobToMove.newStatus = "appliedJobs";
      if (jobToMove.newStatus === "screen") jobToMove.newStatus = "screenJobs";
      if (jobToMove.newStatus === "interview") jobToMove.newStatus = "interviewJobs";
      if (jobToMove.newStatus === "offer") jobToMove.newStatus = "offerJobs";
      if (jobToMove.newStatus === "failed") jobToMove.newStatus = "failedJobs";
      if (jobToMove.newStatus === "archived") jobToMove.newStatus = "archivedJobs";

      const validStatuses = ["applyingJobs", "appliedJobs", "screenJobs", "interviewJobs", "offerJobs", "failedJobs", "archivedJobs"];
      if (!validStatuses.includes(jobToMove.newStatus)) {
        return rejectWithValue("Invalid status " + jobToMove.newStatus);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_DOMAIN}/moveJobStatus`,
        jobToMove,
        { headers: headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  applying: [],
  applied: [],
  screen: [],
  interview: [],
  offer: [],
  archived: [],
  failed: [],
  // Applications the company turned down (the server's `rejected` list). Never left swipes.
  rejected: [],
  selectedCards: [],
  selectedJob: null,
  selectedLoading: false, // For individual job detail loading
  isLoading: false, // For initial applied jobs list loading
  loaded: false, // True if initial data has been successfully fetched at least once
  error: null,
  lastSelectedIndex: -1, // Track last selected index for shift-click
  lastVisitedAt: null, // Track last time user visited MyJobs page
  // What the user cleared from Next steps, by job URL (the server's `upNextCleared`). Null until a
  // server that can keep a clear answers: My Jobs offers clearing only then.
  upNextCleared: null,
  // Clears and restores in flight: a load landing meanwhile keeps the local copy, which already says what they will.
  upNextWrites: 0,
};

export const jobsSlice = createSlice({
  name: "appliedJobs",
  initialState,
  reducers: {
    // { set: { [jobUrl]: entry | null }, writing: +1 | -1 }: a clear or restore applied locally, or put back.
    changeUpNextCleared(state, action) {
      const { set = {}, writing = 0 } = action.payload;
      state.upNextWrites = Math.max(0, state.upNextWrites + writing);
      if (!state.upNextCleared) return;
      for (const [jobUrl, entry] of Object.entries(set)) {
        if (entry) state.upNextCleared[jobUrl] = entry;
        else delete state.upNextCleared[jobUrl];
      }
    },
    // --- NEW: Reducer to add a card to the 'applying' list ---
    addCard(state, action) {
      const jobData = action.payload;

      // Filter the incoming jobData to only include the specified fields
      const newCard = {
        jobUrl: jobData.jobUrl,
        companyName: jobData.companyName,
        jobTitle: jobData.jobTitle,
        jobLocation: jobData.jobLocation,
        payEstimate: jobData.payEstimate,
        jobCategories: jobData.jobCategories,
        jobType: jobData.jobType,
        companyId: jobData.companyId,
      };

      // Add the new card to the beginning of the 'applying' list.
      // This action does NOT trigger a backend update.
      state.applying.unshift(newCard);
    },
    reorder(state, action) {
      const { list, startIndex, endIndex } = action.payload;
      const result = Array.from(state[list]);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      state[list] = result;
    },
    move(state, action) {
      const { sourceList, destinationList, sourceIndex, destinationIndex } =
        action.payload;
      const sourceClone = Array.from(state[sourceList]);
      const destClone = Array.from(state[destinationList]);
      const [removed] = sourceClone.splice(sourceIndex, 1);
      destClone.splice(destinationIndex, 0, removed);
      state[sourceList] = sourceClone;
      state[destinationList] = destClone;
    },
    selectJob(state, action) {
      state.selectedJob = action.payload;
    },
    // A reply through JobHarvest went out for this job's recruiter message: its card says
    // "Replied" at once instead of offering Reply until the next list fetch.
    markJobReplied(state, action) {
      const { jobUrl, repliedAt } = action.payload;
      const at = repliedAt || new Date().toISOString();
      for (const list of STAGE_LISTS) {
        for (const job of state[list] || []) {
          if (job?.jobUrl === jobUrl && job.nextStage) job.nextStage = { ...job.nextStage, repliedAt: at };
        }
      }
      if (state.selectedJob?.jobUrl === jobUrl && state.selectedJob.nextStage) {
        state.selectedJob.nextStage = { ...state.selectedJob.nextStage, repliedAt: at };
      }
    },
    clearSelectedJobInternal(state) {
      state.selectedJob = null;
      if (state.selectedLoading) {
        state.selectedLoading = false;
      }
    },

    toggleCardSelection: (state, action) => {
      const { jobUrl, index } = action.payload;
      if (state.selectedCards.includes(jobUrl)) {
        state.selectedCards = state.selectedCards.filter((id) => id !== jobUrl);
      } else {
        state.selectedCards.push(jobUrl);
      }
      state.lastSelectedIndex = index;
    },
    toggleRangeSelection: (state, action) => {
      const { startIndex, endIndex, jobs } = action.payload;
      const start = Math.min(startIndex, endIndex);
      const end = Math.max(startIndex, endIndex);

      const jobsInRange = jobs.slice(start, end + 1);
      const jobUrlsInRange = jobsInRange.map((job) => job.jobUrl);

      // Toggle all jobs in range
      jobUrlsInRange.forEach((jobUrl) => {
        if (!state.selectedCards.includes(jobUrl)) {
          state.selectedCards.push(jobUrl);
        }
      });
    },
    selectAllJobs: (state, action) => {
      const { jobs } = action.payload;
      state.selectedCards = jobs.map((job) => job.jobUrl);
    },
    clearSelection: (state) => {
      state.selectedCards = [];
      state.lastSelectedIndex = -1;
    },
    clearAllData() {
      return initialState;
    },
    archiveSelectedCards(state) {
      state.selectedCards.forEach((jobUrl) => {
        // Move job from [applied, screen, interview, offer] to archived
        // Skip "applying" stage jobs as they cannot be archived
        ["applied", "screen", "interview", "offer"].forEach(
          (column) => {
            const index = state[column].findIndex(
              (job) => job.jobUrl === jobUrl
            );
            if (index !== -1) {
              const [job] = state[column].splice(index, 1);
              state.archived.push(job);
            }
          }
        );
      });
      // Remove only the jobs that were actually archived from selectedCards
      // Keep applying stage jobs selected so user knows they couldn't be archived
      const archivedJobUrls = [];
      ["applied", "screen", "interview", "offer"].forEach((column) => {
        state[column].forEach((job) => {
          if (state.selectedCards.includes(job.jobUrl)) {
            archivedJobUrls.push(job.jobUrl);
          }
        });
      });
      state.selectedCards = state.selectedCards.filter(jobUrl => {
        // Keep applying stage jobs selected
        const isApplyingJob = state.applying.some(job => job.jobUrl === jobUrl);
        return isApplyingJob;
      });
    },
    deleteSelectedCards(state) {
      state.selectedCards.forEach((jobUrl) => {
        // Find and remove the job from any column
        Object.keys(state).forEach((column) => {
          if (Array.isArray(state[column])) {
            state[column] = state[column].filter(
              (job) => job.jobUrl !== jobUrl
            );
          }
        });
      });
      state.selectedCards = [];
    },
    unarchiveSelectedCards(state) {
      state.selectedCards.forEach((jobUrl) => {
        for (const list of ["archived", "rejected"]) {
          const index = state[list].findIndex((job) => job.jobUrl === jobUrl);
          if (index !== -1) state.applied.push(state[list].splice(index, 1)[0]);
        }
      });
      state.selectedCards = [];
    },
    // --- NEW: Track MyJobs last visit time ---
    setMyJobsLastVisitedNow(state) {
      state.lastVisitedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- fetchAppliedJobData cases ----
      .addCase(fetchAppliedJobData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAppliedJobData.fulfilled, (state, action) => {
        const {
          applying,
          applied,
          screen,
          interview,
          offer,
          archived,
          failed,
          rejected,
        } = action.payload;
        state.applying = applying || [];
        state.applied = applied || [];
        state.screen = screen || [];
        state.interview = interview || [];
        state.offer = offer || [];
        state.archived = archived || [];
        state.failed = failed || [];
        state.rejected = rejected || [];
        if (!state.upNextWrites) state.upNextCleared = action.payload.upNextCleared ?? null;
        state.isLoading = false;
        state.loaded = true;
      })
      .addCase(fetchAppliedJobData.rejected, (state, action) => {
        state.isLoading = false;
        state.loaded = true;
        state.error = action.payload;
      })
      // ---- fetchJobDetails cases ----
      .addCase(fetchJobDetails.pending, (state) => {
        state.selectedLoading = true;
        state.error = null;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        // The details payload is { jobDetails, companyDetails } with no top-level jobUrl, and
        // everything keyed on the open job (the Prep tab's pack, the résumé preview) reads
        // selectedJob.jobUrl. Replacing the job with the bare payload dropped it the moment
        // the details arrived, so keep the url the details were fetched for.
        const { jobUrl } = action.meta.arg;
        // A late answer for a job that is no longer open (another was chosen, or the dialog
        // closed) must neither replace it nor reopen the dialog.
        if (state.selectedJob?.jobUrl !== jobUrl) return;
        state.selectedJob = { ...action.payload, jobUrl };
        state.selectedLoading = false;
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        if (action.payload !== "Request canceled") {
          state.error = action.payload;
        }
        state.selectedLoading = false;
      })

      // ---- Silent refresh cases (no isLoading toggle) ----
      .addCase(refreshAppliedJobDataSilently.fulfilled, (state, action) => {
        const {
          applying,
          applied,
          screen,
          interview,
          offer,
          archived,
          failed,
          rejected,
        } = action.payload || {};
        if (applying) state.applying = applying;
        if (applied) state.applied = applied;
        if (screen) state.screen = screen;
        if (interview) state.interview = interview;
        if (offer) state.offer = offer;
        if (archived) state.archived = archived;
        if (failed) state.failed = failed;
        if (rejected) state.rejected = rejected;
        if (!state.upNextWrites && action.payload) state.upNextCleared = action.payload.upNextCleared ?? null;
        state.loaded = true; // ensure UI has data
      })
      .addCase(refreshAppliedJobDataSilently.rejected, (state, action) => {
        // Do not toggle isLoading for silent refresh; optionally record error
        // state.error = action.payload;
      })

      // ---- If you want to handle updateAppliedJobs silently, you can do so here. ----
      .addCase(updateAppliedJobs.pending, (state) => {
        // No UI loading changes if you want it silent
      })
      .addCase(updateAppliedJobs.fulfilled, (state, action) => {
        // If needed, handle success silently
      })
      .addCase(updateAppliedJobs.rejected, (state, action) => {
        // Optionally handle error—maybe store it, or ignore it silently
        // state.error = action.payload; // Example: log error without UI change
      });
  },
});

// Action creators
export const {
  reorder,
  move,
  addCard,
  selectJob,
  markJobReplied,
  clearSelectedJobInternal,
  toggleCardSelection,
  toggleRangeSelection,
  selectAllJobs,
  clearSelection,
  clearAllData,
  archiveSelectedCards,
  deleteSelectedCards,
  unarchiveSelectedCards,
  setMyJobsLastVisitedNow,
} = jobsSlice.actions;

export default jobsSlice.reducer;

// Active stages first, so a url the server lists twice reads as its active stage, not as archived or failed.
const STAGE_LISTS = ["applying", "applied", "screen", "interview", "offer", "archived", "failed", "rejected"];
const EMPTY_LIST = [];

/**
 * jobUrl (one trailing slash removed) -> the list that job is in. Email screens read it to tell
 * whether the job an email belongs to has moved past that email. Memoized: the Mailbox reads it on
 * every render, and a new object per store change would re-render every row on every action.
 */
export const selectStageByJobUrl = createSelector(
  STAGE_LISTS.map((list) => (state) => state.appliedJobs?.[list] ?? EMPTY_LIST),
  (...lists) => {
    const stageByUrl = new Map();
    lists.forEach((jobs, i) => {
      jobs.forEach((job) => {
        const url = withoutTrailingSlash(job?.jobUrl);
        if (url && !stageByUrl.has(url)) stageByUrl.set(url, STAGE_LISTS[i]);
      });
    });
    return Object.fromEntries(stageByUrl);
  }
);

/** jobUrl (one trailing slash removed) -> the job row, for screens that start from an email. */
export const selectJobByJobUrl = createSelector(
  STAGE_LISTS.map((list) => (state) => state.appliedJobs?.[list] ?? EMPTY_LIST),
  (...lists) => {
    const byUrl = {};
    lists.flat().forEach((job) => {
      const url = withoutTrailingSlash(job?.jobUrl);
      if (url && !byUrl[url]) byUrl[url] = job;
    });
    return byUrl;
  }
);

export const moveCard = (payload) => (dispatch, getState) => {
  // Determine which job is moving and its new status before optimistic update
  const stateBefore = getState();
  const {
    sourceList,
    destinationList,
    sourceIndex,
  } = payload;

  const sourceArrayBefore = stateBefore.appliedJobs?.[sourceList] || [];
  const jobBeingMoved = sourceArrayBefore[sourceIndex];
  const movedJobUrl = jobBeingMoved?.jobUrl;

  // 1. Immediate Redux update (optimistic)
  dispatch(jobsSlice.actions.move(payload));

  // 2. Update backend with the single job's new status
  if (movedJobUrl && sourceList !== destinationList) {
    dispatch(updateAppliedJobs({ jobUrl: movedJobUrl, newStatus: destinationList }));
  }
};

export const reorderCard = (payload) => (dispatch) => {
  // 1. Immediate Redux update (optimistic)
  dispatch(jobsSlice.actions.reorder(payload));
  // 2. No backend call needed for pure reorder since status does not change
};

// 1. archiveSelected
export const archiveSelected = () => (dispatch, getState) => {
  // Capture which jobs will be archived before optimistic update
  const stateBefore = getState();
  const selected = stateBefore.appliedJobs?.selectedCards || [];
  const columnsToArchiveFrom = ["applied", "screen", "interview", "offer"];
  const jobUrlsToArchive = selected.filter((jobUrl) =>
    columnsToArchiveFrom.some((col) =>
      (stateBefore.appliedJobs?.[col] || []).some((job) => job.jobUrl === jobUrl)
    )
  );

  // 1) Optimistic update in Redux
  dispatch(jobsSlice.actions.archiveSelectedCards());

  // 2) Dispatch backend updates per affected job
  jobUrlsToArchive.forEach((jobUrl) =>
    dispatch(updateAppliedJobs({ jobUrl, newStatus: "archived" }))
  );
};

// 2. deleteSelected
// Deletes by URL. This used to rewrite every list from our copy of the state, which could not
// remove the failed jobs the server rebuilds from past attempts (they came back on refresh) and
// could drop jobs the server had moved since our last fetch.
export const deleteSelected = () => async (dispatch, getState) => {
  const jobUrls = getState().appliedJobs?.selectedCards || [];
  if (!jobUrls.length) return;

  // 1) Optimistic update in Redux
  dispatch(jobsSlice.actions.deleteSelectedCards());

  // 2) Persist; if that fails, reload so the page shows what is really still there
  try {
    await backendConnector.deleteJobs(jobUrls);
  } catch (error) {
    logger.error("Error deleting jobs:", error);
    dispatch(refreshAppliedJobDataSilently());
  }
};

// 3. unarchiveSelected
export const unarchiveSelected = () => (dispatch, getState) => {
  // Capture which jobs will be moved to applied before optimistic update
  const stateBefore = getState();
  const selected = stateBefore.appliedJobs?.selectedCards || [];
  const columnsToUnarchiveFrom = ["archived", "rejected"];
  const jobUrlsToApplied = selected.filter((jobUrl) =>
    columnsToUnarchiveFrom.some((col) =>
      (stateBefore.appliedJobs?.[col] || []).some((job) => job.jobUrl === jobUrl)
    )
  );

  // 1) Optimistic update in Redux
  dispatch(jobsSlice.actions.unarchiveSelectedCards());

  // 2) Dispatch backend updates per affected job
  jobUrlsToApplied.forEach((jobUrl) =>
    dispatch(updateAppliedJobs({ jobUrl, newStatus: "applied" }))
  );
};

/**
 * Clears items ({ job, stage, next }) from Next steps, at once, and tells the server. They stay in their
 * stages, and come back on their own when the job moves or a newer message arrives. A refused write puts
 * them back. Resolves to whether the clear was kept.
 */
export const clearFromNextSteps = (items) => (dispatch, getState) => {
  const clear = items.map(({ job, stage, next }) => ({
    jobUrl: withoutTrailingSlash(job.jobUrl), stage, ...(next?.message?.emailId ? { emailId: String(next.message.emailId) } : {}),
  }));
  const at = Date.now();
  const set = Object.fromEntries(clear.map((c) => [c.jobUrl, { stage: c.stage, emailId: c.emailId ?? null, at }]));
  return writeUpNext(dispatch, getState, set, { clear, restore: [] });
};

/** Brings cleared items back to Next steps (the undo). */
export const restoreToNextSteps = (jobUrls) => (dispatch, getState) => {
  const keys = jobUrls.map(withoutTrailingSlash);
  return writeUpNext(dispatch, getState, Object.fromEntries(keys.map((k) => [k, null])), { clear: [], restore: keys });
};

// Each write waits for the one before, so an Undo clicked while its clear is still in flight cannot reach
// the server first and leave the item cleared there.
let upNextQueue = Promise.resolve();

async function writeUpNext(dispatch, getState, set, body) {
  const cleared = getState().appliedJobs.upNextCleared;
  if (!cleared || !Object.keys(set).length) return false;
  const before = Object.fromEntries(Object.keys(set).map((k) => [k, cleared[k] ?? null]));
  dispatch(jobsSlice.actions.changeUpNextCleared({ set, writing: 1 }));
  const send = upNextQueue.then(() => backendConnector.updateUpNext(body));
  upNextQueue = send.catch(() => {});
  try {
    await send;
    dispatch(jobsSlice.actions.changeUpNextCleared({ writing: -1 }));
    return true;
  } catch (error) {
    logger.error("Next steps update failed:", error);
    // Only this write's keys: another may have landed since.
    dispatch(jobsSlice.actions.changeUpNextCleared({ set: before, writing: -1 }));
    return false;
  }
}

export const clearSelectedJob = () => (dispatch) => {
  if (currentJobDetailsAbortController) {
    currentJobDetailsAbortController.abort();
  }
  dispatch(jobsSlice.actions.clearSelectedJobInternal());
};

// --- Helper: On page mount, refresh silently if last visit was > 60s ago ---
export const refreshAppliedJobDataIfStale = () => (dispatch, getState) => {
  const lastVisitedAt = getState().appliedJobs?.lastVisitedAt;
  const now = Date.now();
  if (lastVisitedAt && now - lastVisitedAt > 60_000) {
    return dispatch(refreshAppliedJobDataSilently());
  }
  return Promise.resolve();
};